/**
 * HowItWorksPage — RC 1.49.x.WF rewrite (workflow accuracy pass).
 *
 * Operator-locked structure (RC 1.49.x.WF FINAL UI CLARITY pass):
 *   A. Getting Jobs In
 *   B. Three Ways to Work
 *      1. Manual Review
 *      2. Assisted Review
 *      3. Automation (Flow)
 *   C. Application Flow
 *   D. Key Clarifications
 *
 * In-app walkthrough that explains the NDORFlow workflow to first-
 * time users. Routed at `/how-it-works`; surfaced from the main
 * candidate sidebar AND the user-menu dropdown (redundancy by design).
 *
 * Code-grounded references:
 *   • Add job (URL/manual)    → JobsPage::AddJobModal (URL import + paste)
 *   • Browse jobs             → JobsPage filter bar
 *   • Manual Analyse Fit      → routes/jobs.py::analyze_fit (charges Credits)
 *   • Assisted Review         → routes/automation.py::_auto_score_shared_jobs
 *                              (operator-absorbed; flow_automation_paused=TRUE)
 *   • Automation (Flow)       → core/automation_loop.py::run_tick
 *                              (operator-absorbed; pre-screen → A+B+C; pause toggle)
 *   • Application wizard      → JobApplicationWizard.jsx (4 steps)
 *   • Application pack export → ApplicationPackDetailPage.js
 *
 * Hard rails honoured: zero schema, scoring, governance, prompts,
 * model routing, pricing, or Stripe changes. Pure presentation.
 */
import { useNavigate } from 'react-router-dom';
import {
  FileText, Mail, Target, Package, ShieldCheck, Zap,
  ArrowRight, ArrowLeft, ListChecks, PlusCircle, MousePointerClick,
  Sparkles, Globe, Upload, Gauge, ListFilter, Eye, Shield,
  Info, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

// ─── A. Getting Jobs In ─────────────────────────────────────────────
const GETTING_JOBS_IN = [
  {
    icon: Globe,
    label: 'Add a job by URL',
    copy:
      'Paste any public job URL on /jobs (eFinancialCareers, Greenhouse, ' +
      'company careers pages, etc.). NDORFlow fetches the page, parses the ' +
      'title, company, location and description into structured fields, ' +
      'and dedupes against URLs you have already saved.',
  },
  {
    icon: Upload,
    label: 'Add a job manually',
    copy:
      'Some sites block automated fetching (LinkedIn, Indeed). Paste the ' +
      'job text directly and NDORFlow still structures it for you. The ' +
      'flow falls back to this mode automatically when an import times ' +
      'out or returns insufficient text.',
  },
  {
    icon: ListFilter,
    label: 'Browse the jobs list',
    copy:
      'Open /jobs to see every job in your workspace. Filter by title, ' +
      'company, location, employment type, seniority, or fit-score band. ' +
      'Click any row to open the Job Detail page.',
  },
];

// ─── B. Three Ways to Work ──────────────────────────────────────────
const WAYS_TO_WORK = [
  {
    code: 'manual',
    icon: Eye,
    title: '1. Manual Review',
    bullets: [
      'You control everything — NDORFlow does nothing in the background.',
      'You run Analyse Fit yourself from the Job Detail page.',
      'Credits apply for each manual Analyse Fit.',
      'Available on every plan (Base, Focus, Flow).',
    ],
  },
  {
    code: 'assisted',
    icon: Shield,
    title: '2. Assisted Review',
    bullets: [
      'NDORFlow suggests jobs in a Review Queue based on your Source CV.',
      'You decide what to act on — every application is still your call.',
      'Background Flow automation is OFF in this mode.',
      'Available on Focus and Flow.',
    ],
  },
  {
    code: 'flow',
    icon: Zap,
    title: '3. Automation (Flow)',
    bullets: [
      'Runs automatically in the background.',
      'Pre-screens jobs first using cheap deterministic signals.',
      'Fully analyses the strongest matches (full A+B+C scoring).',
      'Does NOT use your Credits — covered by your Flow subscription.',
      'Can be paused at any time from the Workflow page.',
      'Available on Flow only.',
    ],
  },
];

// ─── C. Application Flow ────────────────────────────────────────────
const APPLICATION_FLOW = [
  {
    icon: MousePointerClick,
    title: 'Apply to job',
    body:
      'When a job catches your eye, click [Apply to this job] on the ' +
      'Job Detail page. This is the only way to launch the four-step ' +
      'application wizard. The wizard never opens on its own.',
  },
  {
    icon: FileText,
    title: 'Choose CV',
    body:
      'Pick the CV NDORFlow will pin into your application pack. Use ' +
      'your Source CV (no charge — your existing fit score carries ' +
      'through), or generate a Tailored Variant (charges Credits) that ' +
      'maps your strongest evidence onto this specific role. Variants ' +
      'never invent experience.',
  },
  {
    icon: Mail,
    title: 'Cover letter',
    body:
      'Generate a cover letter for the job in one click, edit it inline ' +
      'if you want, or skip the letter entirely. The latest draft is ' +
      'locked into the pack on the next step.',
  },
  {
    icon: Target,
    title: 'Analyse Fit',
    body:
      'If you kept your Source CV, the wizard reuses your existing ' +
      'baseline fit — no re-run, no Credits. If you generated a ' +
      'Tailored Variant, click [Analyse Fit] to score the variant and ' +
      'see the "+X vs Source CV" delta. Re-running is optional.',
  },
  {
    icon: ListChecks,
    title: 'Review',
    body:
      'Confirm the CV, cover letter, and fit result that will be locked ' +
      'into the pack. The pack name auto-fills from the job; add private ' +
      'notes if you like.',
  },
  {
    icon: Package,
    title: 'Create pack',
    body:
      'Click [Create Application Pack] and NDORFlow assembles a self-' +
      'contained, immutable record of this application — tailored CV, ' +
      'cover letter, fit score with reasoning, interview prep prompts.',
  },
  {
    icon: ArrowRight,
    title: 'Export',
    body:
      'Download the bundle in one click — [Download PDF] or [Download ' +
      'DOCX] are at the top of the pack page. NDORFlow never auto-' +
      'applies; you submit every application yourself.',
  },
];

// ─── D. Key Clarifications ──────────────────────────────────────────
const KEY_CLARIFICATIONS = [
  {
    icon: Zap,
    label: 'Automation is plan-driven',
    copy:
      'Automation (Flow) runs only on the Flow plan. It is fully managed ' +
      'by NDORFlow — you do not need to click anything to start it. The ' +
      'Workflow page becomes a review surface; you only intervene when ' +
      'you want to act on a match.',
  },
  {
    icon: Eye,
    label: 'Manual overrides automation',
    copy:
      'Picking Manual Review on the Workflow page pauses Flow automation ' +
      'for your account. Background scoring stops immediately — your ' +
      'plan is unchanged, but nothing runs until you switch back to ' +
      'Assisted Review or Automation (Flow).',
  },
  {
    icon: Shield,
    label: 'Assisted is the intermediate mode',
    copy:
      'Assisted Review batches Analyse Fit against your Source CV in the ' +
      'background, capped to a daily budget. Matches above your fit ' +
      'threshold appear in your Review Queue. Background Flow automation ' +
      'is OFF in this mode — Assisted is on its own clock, you trigger ' +
      'each batch by visiting the Workflow page.',
  },
  {
    icon: AlertCircle,
    label: 'No Credits used for background analysis',
    copy:
      'Both Assisted Review and Automation (Flow) are operator-absorbed — ' +
      'your Credit balance is NEVER charged for background scoring. You ' +
      'only spend Credits when you click [Analyse Fit] yourself on a Job ' +
      'Detail page, or when you generate a Tailored CV variant or cover ' +
      'letter inside the application wizard.',
  },
];

// ─── Plan tiers (kept; copy refreshed for naming consistency) ───────
//
// RC 1.49.x — operator-locked 2026-05-07. Three plan codes only:
// Base, Focus, Flow. There is no fourth tier — any external copy
// that names one is stale.
const PLAN_TIERS = [
  {
    code: 'base',
    icon: ShieldCheck,
    name: 'Base',
    accent: 'border-ndor-edge',
    headline: 'For candidates who find roles themselves.',
    bullets: [
      '2 free Analyse Fit per day',
      'Manual Review only',
      'CV upload, structured parse, full application pack composer',
    ],
  },
  {
    code: 'focus',
    icon: Sparkles,
    name: 'Focus',
    accent: 'border-ndor-primary/40',
    headline: 'For candidates who want help surfacing matches.',
    bullets: [
      'Higher monthly Credits allowance',
      'Manual Review + Assisted Review',
      'Assisted Review uses your preferences + Source CV + a deterministic pre-screen first; only prescreen-surviving jobs are analysed with NDOR',
      'Background scoring is operator-absorbed — not charged to your Credits',
      'Capped at a small daily batch so you stay in control',
      'Matches above your fit threshold appear in your Review Queue',
    ],
  },
  {
    code: 'flow',
    icon: Zap,
    name: 'Flow',
    accent: 'border-ndor-gold/40',
    headline: 'For candidates running a serious search at scale.',
    bullets: [
      'Everything in Focus',
      'Manual Review + Assisted Review + Automation (Flow)',
      'Flow is paused by default until you turn it on',
      'Continuous background work uses the shared job pool + your preferences + Source CV + deterministic pre-screen — only survivors reach NDOR',
      'Background Flow scoring is operator-absorbed — not charged to your Credits',
      'Strongest matches surface in your Top Opportunities feed throughout the day',
      'NDORFlow never auto-applies and never auto-tailors',
    ],
  },
];


function SectionHeading({ letter, title, blurb }) {
  return (
    <div className="space-y-1.5" data-testid={`how-it-works-section-${letter.toLowerCase()}`}>
      <p className="text-[10px] font-mono-ui uppercase tracking-[0.22em] text-ndor-gold">
        {letter}.
      </p>
      <h2 className="text-xl sm:text-2xl font-serif-display font-semibold text-ndor-ink">
        {title}
      </h2>
      {blurb && (
        <p className="text-sm text-ndor-ink-secondary max-w-2xl leading-relaxed">
          {blurb}
        </p>
      )}
    </div>
  );
}

export default function HowItWorksPage() {
  const navigate = useNavigate();

  return (
    <div
      className="max-w-4xl w-full mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-12 sm:space-y-14"
      data-testid="how-it-works-page"
    >
      <header className="space-y-3">
        <Badge className="bg-ndor-gold/10 text-ndor-gold border-0 uppercase tracking-[0.18em] text-[10px]">
          Walkthrough
        </Badge>
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-serif-display font-semibold tracking-tight text-ndor-ink"
          data-testid="how-it-works-title"
        >
          How NDORFlow works
        </h1>
        <p className="text-base text-ndor-ink-secondary max-w-2xl leading-relaxed">
          Four short sections from a job URL to an exported application pack.
          Pick the level of automation that matches your search style; NDORFlow
          never auto-applies on any plan, and your Credits are never used for
          background analysis.
        </p>
      </header>

      {/* ─── A. Getting Jobs In ───────────────────────────────────── */}
      <section className="space-y-4" data-testid="how-it-works-getting-jobs">
        <SectionHeading
          letter="A"
          title="Getting Jobs In"
          blurb="Three ways to populate the workspace before any analysis runs."
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {GETTING_JOBS_IN.map((g, i) => {
            const Icon = g.icon;
            return (
              <Card
                key={g.label}
                className="border-ndor-edge"
                data-testid={`how-it-works-getting-jobs-${i + 1}`}
              >
                <CardContent className="p-4 sm:p-5 space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-ndor-gold shrink-0" aria-hidden="true" />
                    <p className="text-sm font-medium text-ndor-ink">{g.label}</p>
                  </div>
                  <p className="text-xs text-ndor-ink-secondary leading-relaxed">{g.copy}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* ─── B. Three Ways to Work ────────────────────────────────── */}
      <section className="space-y-4" data-testid="how-it-works-ways-to-work">
        <SectionHeading
          letter="B"
          title="Three Ways to Work"
          blurb="The Workflow page is the master switch. Pick one — your selection decides whether anything runs in the background."
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {WAYS_TO_WORK.map((w) => {
            const Icon = w.icon;
            return (
              <Card
                key={w.code}
                className="border-ndor-edge h-full"
                data-testid={`how-it-works-mode-${w.code}`}
              >
                <CardContent className="p-4 sm:p-5 flex flex-col gap-3 h-full">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-ndor-gold shrink-0" aria-hidden="true" />
                    <p className="text-sm font-medium text-ndor-ink">{w.title}</p>
                  </div>
                  <ul className="text-xs text-ndor-ink-secondary list-disc pl-4 space-y-1.5 leading-relaxed">
                    {w.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* ─── C. Application Flow ──────────────────────────────────── */}
      <section className="space-y-4" data-testid="how-it-works-application-flow">
        <SectionHeading
          letter="C"
          title="Application Flow"
          blurb="When you decide to act on a job, NDORFlow walks you through seven small steps."
        />
        <div className="space-y-3">
          {APPLICATION_FLOW.map((step, i) => {
            const Icon = step.icon;
            return (
              <Card
                key={step.title}
                className="border-ndor-edge"
                data-testid={`how-it-works-app-step-${i + 1}`}
              >
                <CardContent className="p-4 sm:p-5 flex items-start gap-3 sm:gap-4">
                  <div className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 p-2 shrink-0">
                    <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-ndor-gold" aria-hidden="true" />
                  </div>
                  <div className="space-y-1.5 min-w-0 flex-1">
                    <p className="text-sm font-medium text-ndor-ink">
                      {i + 1}. {step.title}
                    </p>
                    <p className="text-xs sm:text-sm text-ndor-ink-secondary leading-relaxed">
                      {step.body}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* ─── D. Key Clarifications ────────────────────────────────── */}
      <section className="space-y-4" data-testid="how-it-works-clarifications">
        <SectionHeading
          letter="D"
          title="Key Clarifications"
          blurb="Common misunderstandings, settled."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {KEY_CLARIFICATIONS.map((c, i) => {
            const Icon = c.icon;
            return (
              <Card
                key={c.label}
                className="border-ndor-edge bg-ndor-surface-alt/30"
                data-testid={`how-it-works-clarify-${i + 1}`}
              >
                <CardContent className="p-4 sm:p-5 space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-ndor-gold shrink-0" aria-hidden="true" />
                    <p className="text-sm font-medium text-ndor-ink">{c.label}</p>
                  </div>
                  <p className="text-xs text-ndor-ink-secondary leading-relaxed">{c.copy}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <Card className="border-ndor-gold/30 bg-ndor-gold/5" data-testid="how-it-works-never-auto-apply">
          <CardContent className="p-4 sm:p-5 flex items-start gap-3">
            <Info className="h-4 w-4 text-ndor-gold mt-0.5 shrink-0" aria-hidden="true" />
            <p className="text-xs sm:text-sm text-ndor-ink-secondary leading-relaxed">
              <span className="text-ndor-ink font-medium">NDORFlow never auto-applies.</span>
              {' '}Every submission goes out by you, on your schedule. The product
              accelerates analysis, drafting, and packaging — never the send.
            </p>
          </CardContent>
        </Card>
      </section>

      {/* ─── Plan selector ───────────────────────────────────────── */}
      <section className="space-y-4" data-testid="how-it-works-plans-section">
        <div className="space-y-1.5">
          <h2 className="text-xl sm:text-2xl font-serif-display font-semibold text-ndor-ink">
            Pick a plan
          </h2>
          <p className="text-sm text-ndor-ink-secondary">
            You can change this any time from <span className="font-mono-ui">Account → Plan</span>.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {PLAN_TIERS.map((p) => {
            const Icon = p.icon;
            return (
              <Card
                key={p.code}
                className={`border-2 ${p.accent} h-full`}
                data-testid={`how-it-works-plan-${p.code}`}
              >
                <CardContent className="p-4 sm:p-5 flex flex-col gap-3 h-full">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-ndor-gold shrink-0" aria-hidden="true" />
                    <p className="text-sm font-medium text-ndor-ink">{p.name}</p>
                  </div>
                  <p className="text-xs text-ndor-ink-secondary leading-relaxed">
                    {p.headline}
                  </p>
                  <ul className="text-xs text-ndor-ink-secondary list-disc pl-4 space-y-1 leading-relaxed">
                    {p.bullets.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <div className="pt-2">
          <Button
            onClick={() => navigate('/onboarding/plan', { state: { from: '/how-it-works' } })}
            data-testid="how-it-works-pick-plan"
          >
            Pick your plan <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      </section>

      {/* ─── Footer nav ─────────────────────────────────────── */}
      <footer className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 pt-4 border-t border-ndor-edge">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          data-testid="how-it-works-back"
          className="justify-start sm:justify-center"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to dashboard
        </Button>
        <Button
          onClick={() => navigate('/jobs')}
          data-testid="how-it-works-find-jobs"
        >
          Find a job <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
      </footer>
    </div>
  );
}
